/**
 * Shared row-click rule for the list-like rows (container, signed container).
 *
 * A plain click anywhere on a row toggles its selection — but not when it lands
 * on something that owns the click itself (the title, a button, the checkbox,
 * the actions cell), and not on a shift-click (range selection is the parent's
 * job).
 */

/** Elements inside a row that handle their own clicks. */
const INTERACTIVE_SELECTORS = [
  "[data-row-title]",
  "button",
  'input[type="checkbox"]',
  "[data-actions]",
].join(",");

/** True when the row should turn this click into a selection toggle. */
export function isRowSelectionClick(e: React.MouseEvent<HTMLElement>): boolean {
  // Click originated in a React-portaled descendant (Radix DropdownMenu items,
  // dialog overlays, etc.). React still bubbles synthetic events through the
  // React tree, but the portaled UI owns the click — toggling selection here
  // would swallow the menu item's own onClick.
  if (e.target instanceof Node && !e.currentTarget.contains(e.target)) return false;
  if (e.shiftKey) return false;
  return !(e.target as HTMLElement).closest(INTERACTIVE_SELECTORS);
}

/** The `onClick` a selectable row hands to its container element. */
export function rowSelectionToggle(
  isSelected: boolean,
  onSelect?: (isSelected: boolean) => void
): (e: React.MouseEvent<HTMLElement>) => void {
  return (e) => {
    if (!onSelect || !isRowSelectionClick(e)) return;
    e.preventDefault();
    e.stopPropagation();
    onSelect(!isSelected);
  };
}
